import { baseApi } from "@/shared/api";
import { fileResponseHandler } from "@/shared/utils";
import { authApi } from "@/features/auth/api";
import { decrementPendingActionsCount } from "@/features/auth";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import type {
  PersonalSheetDraftResponse,
  AutosaveDraftDto,
  DeleteDraftFileDto,
  CompletePersonalSheetDto,
  CompletePersonalSheetResponse,
  SignPersonalSheetDto,
  SignPersonalSheetResponse,
  PersonalSheetDocumentResponse,
  StagedFile,
} from "../types";

export const personalSheetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPersonalSheetDraft: builder.query<PersonalSheetDraftResponse, void>({
      query: () => ({
        url: "/api/self/personal-sheet/draft/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "PersonalSheetDraft" as const, id: "DRAFT" }],
    }),

    autosavePersonalSheetDraft: builder.mutation<PersonalSheetDraftResponse, AutosaveDraftDto>({
      query: (body) => ({
        url: "/api/self/personal-sheet/draft/",
        method: "PUT",
        body,
        credentials: "include" as const,
      }),
    }),

    deletePersonalSheetDraft: builder.mutation<void, void>({
      query: () => ({
        url: "/api/self/personal-sheet/draft/",
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "PersonalSheetDraft" as const, id: "DRAFT" }],
    }),

    uploadPersonalSheetDraftFile: builder.mutation<StagedFile, FormData>({
      query: (body) => ({
        url: "/api/self/personal-sheet/draft/files/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
    }),

    deletePersonalSheetDraftFile: builder.mutation<void, DeleteDraftFileDto>({
      query: (body) => ({
        url: "/api/self/personal-sheet/draft/files/",
        method: "DELETE",
        body,
        credentials: "include" as const,
      }),
    }),

    completePersonalSheet: builder.mutation<CompletePersonalSheetResponse, CompletePersonalSheetDto>({
      query: ({ sections }) => ({
        url: "/api/self/personal-sheet/complete/",
        method: "POST",
        body: { sections },
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "PersonalSheetDraft" as const, id: "DRAFT" },
        "PendingActions",
        "SelfService",
        "Auth",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex((item) => item.action_required === "fill_personal_sheet");
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),

    previewPersonalSheet: builder.mutation<{ blob: Blob }, void>({
      query: () => ({
        url: "/api/self/personal-sheet/preview/",
        method: "GET",
        credentials: "include" as const,
        responseHandler: fileResponseHandler,
      }),
    }),

    previewWorkerPersonalSheet: builder.mutation<{ blob: Blob }, number>({
      query: (workerId) => ({
        url: `/api/hr/workers/${workerId}/preview-personal-sheet/`,
        method: "GET",
        credentials: "include" as const,
        responseHandler: fileResponseHandler,
      }),
    }),

    getPersonalSheetDocument: builder.query<PersonalSheetDocumentResponse, void>({
      query: () => ({
        url: "/api/self/personal-sheet/document/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "SelfService" as const, id: "PERSONAL_SHEET_DOCUMENT" }],
    }),

    signPersonalSheet: builder.mutation<SignPersonalSheetResponse, SignPersonalSheetDto>({
      query: (body) => ({
        url: "/api/self/personal-sheet/sign/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
      invalidatesTags: ["PendingActions", "SelfService", "Auth"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex((item) => item.action_required === "sign_personal_sheet");
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );
        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );
        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
  }),
});

export const {
  useGetPersonalSheetDraftQuery,
  useAutosavePersonalSheetDraftMutation,
  useDeletePersonalSheetDraftMutation,
  useUploadPersonalSheetDraftFileMutation,
  useDeletePersonalSheetDraftFileMutation,
  useCompletePersonalSheetMutation,
  usePreviewPersonalSheetMutation,
  usePreviewWorkerPersonalSheetMutation,
  useGetPersonalSheetDocumentQuery,
  useSignPersonalSheetMutation,
} = personalSheetApi;
